
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Shield } from "lucide-react";

interface SecurityAlertModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high';
  onSecureAccount?: () => void;
  onIgnore?: () => void;
}

export function SecurityAlertModal({
  open,
  onClose,
  title = "Security Alert",
  description = "We've detected unusual activity on your account.",
  severity = 'medium',
  onSecureAccount,
  onIgnore
}: SecurityAlertModalProps) {
  const bgColors = {
    low: "bg-yellow-50",
    medium: "bg-orange-50",
    high: "bg-red-50"
  };
  
  const iconColors = {
    low: "text-yellow-500",
    medium: "text-orange-500",
    high: "text-red-500"
  };
  
  const borderColors = {
    low: "border-yellow-200",
    medium: "border-orange-200",
    high: "border-red-200"
  };
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className={`sm:max-w-md ${bgColors[severity]} ${borderColors[severity]}`}
      >
        <DialogHeader className="flex flex-row items-center gap-4">
          <div className={`p-2 rounded-full ${bgColors[severity]} border ${borderColors[severity]}`}>
            <AlertTriangle className={`h-6 w-6 ${iconColors[severity]}`} />
          </div>
          <div>
            <DialogTitle className="text-lg">{title}</DialogTitle>
            <DialogDescription className="text-gray-700">
              {description}
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="p-4 bg-white rounded-md border border-gray-200 mb-4">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <Shield className="h-4 w-4 mr-1 text-blue-500" />
            Recommended Security Actions
          </h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center">
              <span className="bg-blue-100 rounded-full h-1.5 w-1.5 mr-1.5"></span>
              Change your password
            </li>
            <li className="flex items-center">
              <span className="bg-blue-100 rounded-full h-1.5 w-1.5 mr-1.5"></span>
              Enable two-factor authentication
            </li>
            <li className="flex items-center">
              <span className="bg-blue-100 rounded-full h-1.5 w-1.5 mr-1.5"></span>
              Sign out from all other devices
            </li>
          </ul>
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row-reverse gap-2">
          <Button
            onClick={() => {
              if (onSecureAccount) onSecureAccount();
              onClose();
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Secure My Account
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              if (onIgnore) onIgnore();
              onClose();
            }}
          >
            I'll Do This Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
